import { __rest } from 'tslib';
import React, { useState, useRef, useEffect, useContext } from 'react';
import { extractFromPath, getObjFromQueryString, inIframe, isValidPhoneFormat } from '@teko/utils';
import { ModalConfirm } from '@teko/ui';
import { logger } from '@teko/log';

var Partner;
(function (Partner) {
    Partner[Partner["IBANKING"] = 0] = "IBANKING";
    Partner[Partner["FEDERATION"] = 1] = "FEDERATION";
    Partner[Partner["CONSUMERAPP"] = 2] = "CONSUMERAPP";
})(Partner || (Partner = {}));

const IBANKING_LOGIN_DATA = 'ibanking_login_data';
const FEDERATION_PARTNER = 'federation';
const CONSUMERAPP_PARTNER = 'consumerapp';
const LOCALSTORAGE_REFRESH_TOKEN = 'REFRESH_TOKEN';
const LOCALSTORAGE_BACK_APP_URL = 'BACK_APP_URL';
const mergeInfo = (login, profile) => {
    return Object.assign(Object.assign(Object.assign({}, login), profile), { userId: login.userId || profile.userId, name: profile.name || login.name, telephone: profile.telephone || login.phone, avatar: login.picture || profile.avatar });
};
const AuthContext = React.createContext(null);
const AuthContextProvider = ({ children, userService, authService, partner, scopes, router, }) => {
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [show, setShow] = useState(false);
    const lastRenewTokenTime = useRef(0);
    const updateProfile = async (profileInput) => {
        const profile = await userService.updateProfile(profileInput);
        const updatedProfile = Object.assign(Object.assign({}, user), profile);
        setUser(updatedProfile);
        return updatedProfile;
    };
    const fetchProfile = async (params = {}, infoToMerged) => {
        try {
            setLoading(true);
            const profile = await userService.myProfile(params);
            setUser(mergeInfo(infoToMerged || authService.getLoginInfo(), profile));
        }
        catch (e) {
            setUser(null);
            logger.error(e);
        }
        finally {
            setLoading(false);
        }
    };
    const _loginWithConsumerApp = async (userInfo) => {
        const loginInfo = await authService.loginWithTerra(userInfo);
        await fetchProfile({}, loginInfo);
    };
    const _loginWithIBanking = async (queries) => {
        const isSacombankMb = partner === 'sacombankmb';
        if (isSacombankMb) {
            queries.signature = queries.signature || queries.data;
            queries.partner_code = queries.partner_code || '12901';
        }
        const body = await authService.loginWithIBanking(queries);
        const { ibanking_info: { partner_code, token: partner_token }, } = body;
        await fetchProfile(partner_code && partner_token
            ? {
                partnerCode: partner_code,
                partnerToken: partner_token,
            }
            : {});
    };
    const _loginWithFederation = async (queries) => {
        const terraIdToken = queries.terraIdToken;
        const authorizer = queries.authorizer;
        if (!terraIdToken || !authorizer) {
            throw new Error('Missing federarion login config');
        }
        const { refresh_token } = await authService.loginWithFederation(terraIdToken, authorizer);
        localStorage === null || localStorage === void 0 ? void 0 : localStorage.setItem(LOCALSTORAGE_REFRESH_TOKEN, refresh_token);
        await fetchProfile({});
    };
    const _refreshToken = async () => {
        const refreshToken = localStorage === null || localStorage === void 0 ? void 0 : localStorage.getItem(LOCALSTORAGE_REFRESH_TOKEN);
        if (!refreshToken) {
            return;
        }
        const { refresh_token } = await authService.refreshToken(refreshToken);
        localStorage === null || localStorage === void 0 ? void 0 : localStorage.setItem(LOCALSTORAGE_REFRESH_TOKEN, refresh_token);
        await fetchProfile({});
    };
    const _getPartnerType = (partnerType) => {
        switch (partnerType) {
            case CONSUMERAPP_PARTNER:
                return Partner.CONSUMERAPP;
            case FEDERATION_PARTNER:
                return Partner.FEDERATION;
            default:
                return Partner.IBANKING;
        }
    };
    const loginWithPartner = async (queries, autoRenew = true, autoRedirect = true) => {
        const { redirect_uri, backAppUrl } = queries, storeData = __rest(queries, ["redirect_uri", "backAppUrl"]);
        sessionStorage.setItem(IBANKING_LOGIN_DATA, JSON.stringify(storeData));
        if (backAppUrl) {
            localStorage.setItem(LOCALSTORAGE_BACK_APP_URL, backAppUrl);
        }
        const _loginWithPartner = async () => {
            try {
                const partnerType = _getPartnerType(queries.partnerType);
                switch (partnerType) {
                    case Partner.FEDERATION: {
                        await _loginWithFederation(queries);
                        break;
                    }
                    case Partner.IBANKING: {
                        await _loginWithIBanking(queries);
                        break;
                    }
                    case Partner.CONSUMERAPP: {
                        await _loginWithConsumerApp(queries.userInfo);
                        break;
                    }
                }
                const isSacombankMb = partner === 'sacombankmb';
                if (router) {
                    if (queries === null || queries === void 0 ? void 0 : queries.redirect_uri) {
                        const extractLink = extractFromPath(queries.redirect_uri);
                        typeof extractLink === 'object'
                            ? router.push(extractLink, queries.redirect_uri)
                            : router.push(extractLink);
                        return;
                    }
                    if (autoRedirect) {
                        if (isSacombankMb)
                            return setShow(true);
                        router.push('/');
                    }
                }
                else {
                    console.warn('auth-react: router is not set');
                }
            }
            catch (e) {
                logger.error(e);
                if (window.location.pathname.includes('/login'))
                    alert('Đăng nhập thất bại!');
            }
        };
        _loginWithPartner();
        if (autoRenew) {
            setInterval(async () => {
                const now = new Date().getTime();
                const diff = now - lastRenewTokenTime.current;
                if (diff > 3000000) {
                    lastRenewTokenTime.current = now;
                    const partnerType = _getPartnerType(queries.partnerType);
                    switch (partnerType) {
                        case Partner.FEDERATION: {
                            await _refreshToken();
                            break;
                        }
                        case Partner.IBANKING: {
                            _loginWithPartner();
                            break;
                        }
                    }
                }
            }, 3000);
        }
    };
    const initIam = async () => {
        if (partner)
            return;
        await authService.initialize(scopes);
        const loggedInUser = authService.getLoginInfo();
        if (loggedInUser === null || loggedInUser === void 0 ? void 0 : loggedInUser.userId) {
            await fetchProfile();
            const previousLocation = authService.getAndClearBeforeLoginUrl();
            if (previousLocation && router) {
                router.push(previousLocation);
            }
            else if (!router) {
                console.warn('auth-react: router is not set');
            }
        }
        else {
            setLoading(false);
        }
    };
    useEffect(() => {
        if (partner) {
            try {
                const queries = sessionStorage.getItem(IBANKING_LOGIN_DATA);
                if (queries) {
                    const queriesObj = JSON.parse(queries);
                    if (!window.location.pathname.includes('/login') &&
                        Object.keys(queriesObj).length) {
                        if (queriesObj.partnerType === FEDERATION_PARTNER)
                            _refreshToken();
                        else
                            loginWithPartner(queriesObj, true, false);
                    }
                }
            }
            catch (e) {
                logger.error('Relogin ibanking false', e);
            }
        }
        else {
            initIam();
        }
    }, []);
    const { challenge } = router.query;
    useEffect(() => {
        if (challenge && !router.route.includes('/login')) {
            authService.requestLogout(challenge);
        }
    }, [challenge]);
    return (React.createElement(AuthContext.Provider, { value: {
            user,
            loading,
            loginWithPartner,
            authService,
            userService,
            updateProfile,
            initIam,
        } },
        React.createElement(ModalConfirm, { show: show, okText: "\u0110\u1ED3ng \u00FD", ok: () => {
                router === null || router === void 0 ? void 0 : router.push('/');
                setShow(false);
            }, cancelText: "Quay l\u1EA1i", cancel: () => {
                window.location.href = '/?action=backtoapp';
            }, message: "D\u1ECBch v\u1EE5 Mua s\u1EAFm VNSHOP \u0111\u01B0\u1EE3c cung c\u1EA5p b\u1EDFi VNPAY. Chi ti\u1EBFt li\u00EAn h\u1EC7 hotline VNPAY: 1900555577 \u0111\u1EC3 \u0111\u01B0\u1EE3c h\u1ED7 tr\u1EE3." }),
        children));
};
function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be inside a AuthContextProvider with a state value');
    }
    return context;
}

const IBANKING_CHANNEL = 'ibanking-channel';
const setIBankingChannel = (channel) => {
    localStorage.setItem(IBANKING_CHANNEL, channel || '');
};
const isIBankingIFrame = () => {
    return (typeof window !== 'undefined' && !!localStorage.getItem(IBANKING_CHANNEL));
};

const useLogin = ({ challenge, partner, router, showPhoneLogin: initShowPhone = false, }) => {
    const { loginWithPartner, user, authService, userService } = useAuth();
    const [loading, setLoading] = useState(true);
    const [phone, setPhone] = useState('');
    const [noti, setNoti] = useState({
        show: false,
        message: '',
    });
    const [isSentOTP, setIsSentOTP] = useState(false);
    const [showPhoneLogin, setShowPhoneLogin] = useState(initShowPhone);
    const [canEditPhone, setCanEditPhone] = useState(true);
    useEffect(() => {
        const cpRefCode = sessionStorage.getItem('ref_code');
        if (cpRefCode) {
            userService
                .getRefCodeInfo(cpRefCode)
                .then((res) => {
                var _a;
                setCanEditPhone(false);
                setPhone(((_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.telephone) || '');
            })
                .catch((e) => {
                console.error('Get ref code error', e);
            });
        }
    }, []);
    useEffect(() => {
        const handlePostMessageLogin = (event) => {
            if (typeof event.data === 'object' &&
                event.data.data &&
                event.data.signature) {
                loginWithPartner(event.data);
            }
        };
        if (!partner) {
            if (!challenge || user) {
                router === null || router === void 0 ? void 0 : router.push('/');
                return;
            }
            authService
                .requestLogin(challenge)
                .then((response) => {
                setLoading(false);
                if (response.skip && response.redirect_to) {
                    window.location.replace(response.redirect_to);
                }
            })
                .catch((err) => {
                console.error(err);
                router === null || router === void 0 ? void 0 : router.push('/');
            });
        }
        else {
            const queries = getObjFromQueryString(window.location.search);
            if (Object.keys(queries).length) {
                setIBankingChannel(queries.channel);
                loginWithPartner(queries);
            }
            else {
                if (inIframe()) {
                    setIBankingChannel('any');
                }
                window.addEventListener('message', handlePostMessageLogin, false);
            }
        }
        return () => {
            window.removeEventListener('message', handlePostMessageLogin);
        };
    }, [challenge]);
    const sendOTP = async (message) => {
        var _a;
        setLoading(true);
        try {
            const res = await authService.sendOTP(challenge || '', phone);
            if (res.success) {
                setNoti({ show: true, message });
                setIsSentOTP(true);
            }
            else
                setNoti({
                    show: true,
                    message: 'Đã có lỗi xảy ra, vui lòng thử lại',
                });
        }
        catch (error) {
            console.error(error);
            if (((_a = error === null || error === void 0 ? void 0 : error.error) === null || _a === void 0 ? void 0 : _a.code) === 1800 || (error === null || error === void 0 ? void 0 : error.code) === 1800) {
                setNoti({
                    show: true,
                    message: 'Bạn đã đăng nhập quá số lần cho phép trong ngày',
                });
            }
            else {
                setNoti({
                    show: true,
                    message: 'Đã có lỗi xảy ra, vui lòng thử lại',
                });
            }
        }
        finally {
            setLoading(false);
        }
    };
    const handleOTPChange = (otp) => {
        setLoading(true);
        authService
            .loginIAMWithPhone(challenge || '', phone, otp)
            .then((res) => {
            if (res.redirect_to)
                window.location.href = res.redirect_to;
            else {
                setLoading(false);
                setNoti({ show: true, message: 'Mã OTP không chính xác' });
            }
        })
            .catch(() => {
            setLoading(false);
            setNoti({ show: true, message: 'Mã OTP không chính xác' });
        });
    };
    const handlePhoneChange = (number) => {
        setPhone(number);
        setNoti(isValidPhoneFormat(number)
            ? { show: false, message: '' }
            : { show: true, message: 'Vui lòng nhập đúng định dạng số điện thoại' });
    };
    const handleSendOTP = (message) => {
        if (!phone) {
            setNoti({
                show: true,
                message: 'Vui lòng nhập số điện thoại',
            });
            return;
        }
        if (noti.show)
            return;
        sendOTP(message);
    };
    const handleResendOTP = (message) => {
        sendOTP(message);
    };
    const handleIamResponse = (data) => {
        if (data.error) {
            throw new Error(data.error);
        }
        if (data.redirect_to) {
            window.location.assign(data.redirect_to);
        }
    };
    const loginWithPhone = () => {
        setShowPhoneLogin((prev) => {
            setNoti((noti) => (Object.assign(Object.assign({}, noti), { show: false })));
            return !prev;
        });
    };
    const loginWithFacebook = async (authResponse) => {
        try {
            if (!authResponse) {
                throw new Error('Invalid response from Facebook');
            }
            const { data } = await authService.loginSocialIAM(challenge || '', authResponse.accessToken, 'facebook');
            handleIamResponse(data);
        }
        catch (e) {
            console.error(e.message);
            alert('Đăng nhập với Facebook thất bại!');
        }
    };
    const loginWithGoogle = async (gapiInstance) => {
        try {
            const googleUser = await gapiInstance.signIn({
                prompt: 'select_account',
            });
            const { data } = await authService.loginSocialIAM(challenge || '', googleUser.getAuthResponse(true).access_token, 'google');
            handleIamResponse(data);
        }
        catch (e) {
            console.error(e);
            alert('Đăng nhập với Google thất bại!');
        }
    };
    return {
        loading,
        loginWithFacebook,
        loginWithGoogle,
        loginWithPhone,
        handleOTPChange,
        handleSendOTP,
        handleResendOTP,
        handlePhoneChange,
        phone,
        noti,
        isSentOTP,
        showPhoneLogin,
        canEditPhone,
    };
};

export { AuthContextProvider, isIBankingIFrame, useAuth, useLogin };
